import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAlertPreferences, useUpdateAlertPreference } from '../hooks/useSites'

const ALERT_TYPE_LABELS: Record<string, { label: string; description: string }> = {
  regression: {
    label: 'SEO Regression',
    description: 'Score drops between crawls',
  },
  recovery: {
    label: 'SEO Recovery',
    description: 'Score improvements between crawls',
  },
  crawl_failure: {
    label: 'Crawl Failure',
    description: 'Scheduled crawl failed to complete',
  },
  keyword_drop: {
    label: 'Keyword Drop',
    description: 'Tracked keywords dropped more than 5 positions',
  },
  page_change: {
    label: 'Page Changes',
    description: 'High-value pages changed between crawls',
  },
}

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  chat: 'Google Chat',
  slack: 'Slack',
}

export function AlertSettingsPage() {
  const { data, isLoading } = useAlertPreferences()
  const updatePreference = useUpdateAlertPreference()

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold">Alert Settings</h1>
          <p className="text-muted-foreground mt-1">Configure which alerts you receive and how.</p>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const preferences = data?.preferences ?? []

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Alert Settings</h1>
        <p className="text-muted-foreground mt-1">
          Configure which alerts you receive and through which channels.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Notification Channels</CardTitle>
          <CardDescription>
            Toggle notifications per alert type and delivery channel. Channels only work
            when the corresponding webhook URL is configured.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Header row */}
          <div className="grid grid-cols-[1fr_80px_80px_80px] gap-4 border-b pb-3 mb-4">
            <div className="text-sm font-medium text-muted-foreground">Alert Type</div>
            {['email', 'chat', 'slack'].map(channel => (
              <div key={channel} className="text-sm font-medium text-muted-foreground text-center">
                {CHANNEL_LABELS[channel]}
              </div>
            ))}
          </div>

          {/* Preference rows */}
          <div className="space-y-4">
            {preferences.map(pref => {
              const meta = ALERT_TYPE_LABELS[pref.type]
              if (!meta) return null

              return (
                <div key={pref.type} className="grid grid-cols-[1fr_80px_80px_80px] gap-4 items-center">
                  <div>
                    <div className="text-sm font-medium">{meta.label}</div>
                    <div className="text-xs text-muted-foreground">{meta.description}</div>
                  </div>
                  {pref.channels.map(ch => (
                    <div key={ch.channel} className="flex justify-center">
                      <Switch
                        checked={!!ch.enabled}
                        onCheckedChange={(checked) => {
                          updatePreference.mutate({
                            alertType: pref.type,
                            channel: ch.channel,
                            enabled: checked,
                          })
                        }}
                        aria-label={`${meta.label} via ${CHANNEL_LABELS[ch.channel]}`}
                      />
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Channel Setup</CardTitle>
          <CardDescription>
            Webhook URLs are configured as environment variables on the server.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <Label className="font-medium min-w-[100px]">Email</Label>
              <span className="text-muted-foreground">
                Set the alert email on each site's Schedule tab. Requires SMTP2GO_API_KEY.
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Label className="font-medium min-w-[100px]">Google Chat</Label>
              <span className="text-muted-foreground">
                Set GOOGLE_CHAT_WEBHOOK_URL as a Cloudflare secret.
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Label className="font-medium min-w-[100px]">Slack</Label>
              <span className="text-muted-foreground">
                Set SLACK_WEBHOOK_URL as a Cloudflare secret.
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
